import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getUserProfile,
  updateUserProfile,
  getOrganizationProfile,
  uploadWelcomeVideo,
  deleteWelcomeVideo,
} from "../infrastructure/userProfileService";
import { UpdateProfileRequest } from "../dtos/userProfile"
import { useDispatch } from "react-redux"
import { AppDispatch } from "@/store"
import { refreshToken } from "@/modules/auth/store/authSlice"
import { getOrganizationById } from "@/modules/organizations/infrastructure/organization-service"

export function useUserProfile() {
  return useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfile,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient()
  const dispatch = useDispatch<AppDispatch>()

  return useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      const result = await updateUserProfile(data)
      await dispatch(refreshToken())
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userProfile"] })
    },
  })
}

export function useOrganization(organizationId: string | undefined) {
  return useQuery({
    queryKey: ["organization", organizationId],
    queryFn: () => getOrganizationById(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export function useOrganizationProfile(organizationId: string | undefined) {
  return useQuery({
    queryKey: ["organizationProfile", organizationId],
    queryFn: () => getOrganizationProfile(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export function useUploadWelcomeVideo(organizationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (videoFile: File) =>
      uploadWelcomeVideo(organizationId, videoFile),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organizationProfile", organizationId],
      })
    },
  })
}

export function useDeleteWelcomeVideo(organizationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteWelcomeVideo(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organizationProfile", organizationId],
      })
    },
  })
}
